import { defineStore } from 'pinia';
import { ref, computed, onMounted } from 'vue';
import { get } from '@/core/apis';
import { getExpiryTime, getWarningTime } from '@/service/AutoLogoutService';
import { deleteLSItem, getLSItem, setLSItem } from '@/service/NimbusLSService';
import { useAutoLogout } from '@/stores/auto-logout';
import { getCurrencySetting } from '@/service/CurrencyService';
import {
    INVOICE_COLUMN_DATATABLE_SETTING_KEY_EUP,
    INVOICE_COLUMN_DATATABLE_SETTING_KEY_SP,
} from '@/components/invoice/invoiceConstants';
import { paymentMethodStore } from '@/stores/datatables/paymentMethodStore';
import { getDefaultAccountSettings } from '@/service/AccountService';
import { getDefaultAccountCountry } from '@/service/UserService';
import { LOCAL_STORAGE_REST_COUNTRIES_API } from '@/service/RestCountriesExternalApiService';

export const useUserStore = defineStore(
    'user-store',
    () => {
        /**
         * State
         */
        const userData = ref({});
        const userTimezone = ref({});
        const userSettings = ref({});
        const latestTokenRequest = ref({});
        const currencySettings = ref({});
        // LocalStorage
        const
            LS_TIMEZONE = 'nimbus-timezone',
            LS_SETTINGS = 'nimbus-settings';

        /**
         * Getters
         */
        const getUserTimezoneDisplay = computed(() => {
            return userTimezone.value ? `${userTimezone.value.abbr} ${userTimezone.value.identifier} ${userTimezone.value.name}` : null;
        });

        /**
         * get the email domain of logged user
         * @type {ComputedRef<*>}
         */
        const getUserDataEmailDomain = computed(() => userData.value.emailDomain);

        /**
         * Checked if the Logged User is an Emersion Account
         *
         * @type {ComputedRef<*>}
         */
        const isEmersionUser = computed(() => userData.value.isEmersion);

        const isBankAccountTypeRequired = computed(() => userData.value.isBankAccountTypeRequired)

        const isEup = computed(() => userData.value.isEndUser);

        const nimbusDatatableInvoiceSettingKey = computed(() => {
            return (userData.value.isEndUser)
                ? INVOICE_COLUMN_DATATABLE_SETTING_KEY_EUP
                : INVOICE_COLUMN_DATATABLE_SETTING_KEY_SP;
        });

        const accountId = computed(() => userData.value.accountId);
        const DEFAULT_INVOICE_ID_SHOW_BOTH = 0;
        const DEFAULT_INVOICE_ID_SHOW_INVOICE_ID = 1;
        const INVOICE_ID_SHOW_ORIGINAL_INVOICE_ID = 2;
        /**
         * Actions
         */

        /** Set the details for user upon authenticated **/
        const setUserAuthenticatedDetails = (data) => {
            userData.value = data;
        };

        /** Set latest token request */
        const setLatestTokenRequest = (data) => {
            latestTokenRequest.value = data;
        }

        /**
         * Set the user timezone upon login
         *
         * @returns {Promise<void>}
         */
        const getUserTimezone = async () => {
            try {
                const storedTimezone = getLSItem(LS_TIMEZONE);
                if (storedTimezone) {
                    userTimezone.value = JSON.parse(storedTimezone);
                    return;
                }

                const res = await get('account/timezone');
                userTimezone.value = res;
                setLSItem(LS_TIMEZONE, JSON.stringify(res));
            } catch (error) {
                console.error(error);
            }
        };

        /**
         * Get the user account settings
         *
         * @returns {Promise<void>}
         */
        const getUserAccountSettings = async () => {
            try {
                const autoLogoutSTore = useAutoLogout();
                deleteLSItem('auto-logout');

                const storedSettings = getLSItem(LS_SETTINGS);
                if (storedSettings) {
                    userSettings.value = JSON.parse(storedSettings);
                    autoLogoutSTore.setSettingsTimer();
                    return;
                }

                const response = await get('account/setting');
                userSettings.value = response.data;
                setLSItem(LS_SETTINGS, JSON.stringify(response.data))
                autoLogoutSTore.setSettingsTimer();

                // Payment Method
                await paymentMethodStore().getUsablePaymentMethod();
            } catch (error) {
                console.error(error);
            }
        };

        /**
         * Get a single setting object via key
         *
         * @param key
         * @returns {*}
         */
        const getSettingViaKey = (key) => {
            return userSettings.value?.find((v) => {
                return v.setting && v.setting.key === key;
            });
        };

        const getNimbusDataTableInvoiceIdColumnSettingViaKey = () => {
            const setting = getSettingViaKey(nimbusDatatableInvoiceSettingKey.value);
            if (setting) {
                return setting.value;
            }

            // Default value - if setting is not set for an account
            return (userData.value.isEndUser)
                ? DEFAULT_INVOICE_ID_SHOW_INVOICE_ID // EUP
                : DEFAULT_INVOICE_ID_SHOW_BOTH // SP
        };
        
        const setUserSettingInvoiceId = (newValue) => {
            const setting = getSettingViaKey(nimbusDatatableInvoiceSettingKey.value)
            setting.value = newValue;
        }

        const getALExpirationTime = () => {
            const expirationData = getSettingViaKey('nimbus.session-expiry-duration');
            if (expirationData) {
                return parseInt(expirationData.value);
            }

            return getExpiryTime();
        };

        const getALWarningTime = () => {
            const expirationData = getSettingViaKey('nimbus.idle-warning-duration');
            if (expirationData) {
                return parseInt(expirationData.value);
            }

            return getWarningTime();
        };

        /**
         * Function to clear the data upon logout
         */
        const clearUserData = () => {
            userData.value = {};
            userTimezone.value = {};
            userSettings.value = {};
            latestTokenRequest.value = {};
        };
        

        const getCurrency = () => {
            currencySettings.value = getCurrencySetting()
        }

        return {
            /**
             * State
             */
            userData,
            userTimezone,
            userSettings,
            latestTokenRequest,
            currencySettings,

            /**
             * Getters
             */
            getUserTimezoneDisplay,
            getUserDataEmailDomain,
            isEmersionUser,
            isBankAccountTypeRequired,
            isEup,
            DEFAULT_INVOICE_ID_SHOW_BOTH,
            INVOICE_ID_SHOW_ORIGINAL_INVOICE_ID,
            accountId,
            nimbusDatatableInvoiceSettingKey,

            /**
             * Actions
             */
            setLatestTokenRequest,
            setUserAuthenticatedDetails,
            setUserSettingInvoiceId,
            getUserTimezone,
            getUserAccountSettings,
            getSettingViaKey,
            clearUserData,
            getALExpirationTime,
            getALWarningTime,
            getCurrency,
            getNimbusDataTableInvoiceIdColumnSettingViaKey
        };
    },
    {
        persist: true,
    }
);
